import {
    Alert,
    Button,
    Card,
    Center,
    Group,
    Stack,
    Text,
    ThemeIcon,
} from '@mantine/core';
import { AiOutlineCreditCard, AiOutlineCheckCircle, AiOutlineWarning } from 'react-icons/ai';
import { useBillingSetup } from '../hooks/useBillingSetup';

interface BillingSetupCardProps {
    variant?: 'full' | 'banner';
}

export const BillingSetupCard = ({ variant = 'full' }: BillingSetupCardProps) => {
    const { setupBilling, isSettingUp, isComplete, error, hasCustomerId } = useBillingSetup();

    if (hasCustomerId || isComplete) {
        if (variant === 'banner') return null;
        return (
            <Center py="xl">
                <Group gap="sm">
                    <AiOutlineCheckCircle size="1.4em" color="var(--mantine-color-green-6)" />
                    <Text c="green" fw={500}>Billing profile is set up.</Text>
                </Group>
            </Center>
        );
    }

    if (variant === 'banner') {
        return (
            <Alert
                variant="light"
                color="blue"
                title="Complete your billing setup"
                icon={<AiOutlineCreditCard />}
                mb="md"
            >
                <Group justify="space-between" align="center">
                    <Text size="sm">
                        Set up your billing profile to access subscriptions and payment features.
                    </Text>
                    <Button
                        size="xs"
                        onClick={setupBilling}
                        loading={isSettingUp}
                    >
                        Set Up Billing
                    </Button>
                </Group>
                {error && <Text size="xs" c="red" mt="xs">{error}</Text>}
            </Alert>
        );
    }

    return (
        <Center py="xl" px="md">
            <Card shadow="sm" padding="xl" radius="md" withBorder maw={480} w="100%">
                <Stack gap="lg" align="center">
                    <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
                        <AiOutlineCreditCard size="1.4em" />
                    </ThemeIcon>

                    <Stack gap="xs" align="center">
                        <Text fw={700} size="xl" ta="center">Set Up Your Billing Profile</Text>
                        <Text size="sm" c="dimmed" ta="center">
                            To access billing features like subscriptions and payment methods,
                            we need to create your billing profile. This is a one-time setup.
                        </Text>
                    </Stack>

                    {error && (
                        <Alert color="red" variant="light" icon={<AiOutlineWarning />} w="100%">
                            {error}
                        </Alert>
                    )}

                    <Button
                        size="md"
                        onClick={setupBilling}
                        loading={isSettingUp}
                        fullWidth
                    >
                        Set Up Billing
                    </Button>
                </Stack>
            </Card>
        </Center>
    );
};
